import { Card, IconTile, type StatusTone } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BadgeCheck,
  BarChart3,
  CalendarClock,
  ChevronRight,
  Clock,
  Eye,
  Flame,
  Inbox,
  Layers,
  Package,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Avatar } from "../../components/Avatar";
import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { RequestStatusChip } from "../../components/Status";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { daysUntil, formatRelative, secondsSince } from "../../lib/format";
import { useSession } from "../../lib/session";

type Dashboard = components["schemas"]["DashboardOut"];
type Card_ = components["schemas"]["ActiveCardOut"];

/** CP Dashboard (core doc 04 P1; locked screens 01/05). Every number is a stored record; signals
 *  that need performance learning or AI production show their honest state (locked decision 10). */
export function DashboardPage() {
  const { t } = useTranslation();
  const me = useSession();
  const q = useQuery({ queryKey: ["dashboard"], queryFn: () => call<Dashboard>(api.GET("/app/v1/dashboard")) });
  const first = me.name.split(" ")[0] ?? me.name;
  return (
    <QueryState query={q}>
      {(d) => (
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0 space-y-8">
            <PageHeader
              titleKey={`home.greeting_${d.greeting_part}`}
              values={{ name: first }}
              subtitle={t("home.subtitle")}
              brandLine={t("home.brand_line")}
            />
            <Section titleKey="dashboard.attention" to="/requests">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Tile tone="planning" icon={<Inbox className="size-6" />} n={d.tiles.new_requests ?? 0}
                  labelKey="dashboard.tile_new" subKey="dashboard.tile_new_sub" to="/requests?tab=new" />
                <Tile tone="review" icon={<Eye className="size-6" />} n={d.tiles.in_review ?? 0}
                  labelKey="dashboard.tile_review" subKey="dashboard.tile_review_sub" to="/reviews" />
                <Tile tone="warning" icon={<Clock className="size-6" />} n={d.tiles.due_today ?? 0}
                  labelKey="dashboard.tile_due" subKey="dashboard.tile_due_sub" to="/requests?tab=all" />
                <Tile tone="success" icon={<BadgeCheck className="size-6" />} n={d.tiles.recently_approved ?? 0}
                  labelKey="dashboard.tile_approved" subKey="dashboard.tile_approved_sub" to="/reviews" />
              </div>
            </Section>
            <Section titleKey="dashboard.active" to="/projects" actionKey="dashboard.view_all">
              {d.active.length === 0 ? (
                <Card className="p-8 text-center text-[14px] text-ink-2">{t("dashboard.active_empty")}</Card>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
                  {d.active.map((c) => (
                    <ActiveCard key={c.display_id} c={c} tz={me.time_zone} />
                  ))}
                </div>
              )}
            </Section>
          </div>
          <aside className="space-y-6">
            <CreativePulse d={d} />
            <ProductionPulse d={d} />
            <RecentActivity d={d} />
          </aside>
        </div>
      )}
    </QueryState>
  );
}

function Section({ titleKey, to, actionKey, children }: { titleKey: string; to: string; actionKey?: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <Link to={to} className="group inline-flex items-center gap-1 text-[15px] font-semibold uppercase tracking-[0.06em] text-violet-text">
          {t(titleKey)}
          <ChevronRight aria-hidden="true" className="size-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
        {actionKey && (
          <Link to={to} className="text-[14px] font-medium text-violet-text hover:underline">
            {t(actionKey)}
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function Tile({ tone, icon, n, labelKey, subKey, to }: {
  tone: StatusTone; icon: ReactNode; n: number; labelKey: string; subKey: string; to: string;
}) {
  const { t } = useTranslation();
  const subColor: Record<string, string> = {
    planning: "text-[var(--status-info)]", review: "text-magenta-text", warning: "text-[var(--status-warning)]",
    success: "text-[var(--status-success)]",
  };
  return (
    <Link to={to} className="block rounded-[var(--radius-card)] border border-line bg-surface p-5 shadow-[var(--shadow-card)] transition-colors hover:border-[var(--border-strong)]">
      <div className="flex items-center gap-4">
        <IconTile tone={tone}>{icon}</IconTile>
        <div className="text-[32px] font-semibold leading-none text-ink">{n}</div>
      </div>
      <div className="mt-3 text-[15px] font-medium text-ink">{t(labelKey)}</div>
      <div className={`mt-0.5 text-[13px] ${subColor[tone] ?? "text-ink-2"}`}>{t(subKey)}</div>
    </Link>
  );
}

function ActiveCard({ c, tz }: { c: Card_; tz: string }) {
  const { t } = useTranslation();
  const days = daysUntil(c.deadline, tz);
  const dueKey = days < 0 ? "dashboard.due_overdue" : days === 0 ? "dashboard.due_today" : days === 1 ? "dashboard.due_tomorrow" : "dashboard.due_in";
  const deliverables = c.deliverables.reduce((n, d) => n + d.quantity, 0);
  return (
    <Link to={`/requests/${c.display_id}`} className="group block overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
      {/* Poster appears once an approved or in-review asset exists (M1-7/M1-9); never stock imagery. */}
      <div className="relative flex aspect-[16/10] items-center justify-center bg-elevated">
        <div aria-hidden="true" className="absolute inset-0 bg-[image:var(--gradient-signature)] opacity-[0.10]" />
        <Package aria-hidden="true" className="relative size-10 text-ink-2" strokeWidth={1.4} />
        <span className="absolute bottom-2 left-2 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white">
          {t("dashboard.no_poster_yet")}
        </span>
      </div>
      <div className="space-y-2 p-4">
        <div className="text-[12px] text-ink-2">{c.display_id}</div>
        <div className="truncate text-[16px] font-semibold text-ink">{c.product_name}</div>
        <div className="truncate text-[13px] text-ink-2">
          {c.hypothesis ?? t(`requests.objective_${c.objective.toLowerCase()}`)}
        </div>
        <RequestStatusChip status={c.status} />
        <div className="flex items-center gap-2 text-[13px] text-ink-2">
          <Layers aria-hidden="true" className="size-4" />
          {c.assets ? t("dashboard.assets_of", { done: c.assets.done, total: c.assets.total }) : t("dashboard.deliverables_n", { count: deliverables })}
        </div>
        <div className={`flex items-center gap-2 text-[13px] ${days <= 0 ? "text-[var(--status-danger)]" : "text-ink-2"}`}>
          <CalendarClock aria-hidden="true" className="size-4" />
          {t(dueKey, { count: days })}
        </div>
      </div>
    </Link>
  );
}

function CreativePulse({ d }: { d: Dashboard }) {
  const { t } = useTranslation();
  const approved = Number(d.creative_pulse.approved_this_week?.count ?? 0);
  return (
    <Card className="p-5">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold uppercase tracking-[0.06em] text-ink">
        <Activity aria-hidden="true" className="size-5 text-cyan" />
        {t("dashboard.creative_pulse")}
      </h2>
      <ul className="mt-4 divide-y divide-[var(--border-default)]">
        <PulseRow icon={<Flame className="size-5 text-[var(--status-warning)]" />} value={String(approved)}
          title={t("dashboard.pulse_approved", { count: approved })} />
        <PulseRow icon={<TrendingUp className="size-5 text-[var(--status-success)]" />} value="—"
          title={t("dashboard.pulse_signals")} sub={t("dashboard.arrives_with_learning")} />
        <PulseRow icon={<TriangleAlert className="size-5 text-[var(--status-warning)]" />} value="—"
          title={t("dashboard.pulse_fatigue")} sub={t("dashboard.arrives_with_learning")} />
      </ul>
    </Card>
  );
}

function PulseRow({ icon, value, title, sub }: { icon: ReactNode; value: string; title: string; sub?: string }) {
  return (
    <li className="flex items-center gap-4 py-3">
      <span aria-hidden="true">{icon}</span>
      <span className="w-8 text-[24px] font-semibold text-ink">{value}</span>
      <span className="min-w-0">
        <span className="block text-[14px] text-ink">{title}</span>
        {sub && <span className="block text-[12px] text-ink-2">{sub}</span>}
      </span>
    </li>
  );
}

function ProductionPulse({ d }: { d: Dashboard }) {
  const { t } = useTranslation();
  const p = d.production_pulse;
  const cells: { key: string; label: string; value: string; sub: string }[] = [
    { key: "image", label: t("nav.image_studio"), value: "—", sub: t("honest.awaiting_ai_production_phase") },
    { key: "video", label: t("nav.video_studio"), value: "—", sub: t("honest.awaiting_ai_production_phase") },
    { key: "shoot", label: t("nav.shooting"), value: String(p.shooting?.count ?? 0), sub: t("dashboard.phase_scheduled") },
    { key: "edit", label: t("nav.editor"), value: String(p.editing?.count ?? 0), sub: t("dashboard.phase_in_progress") },
  ];
  return (
    <Card className="p-5">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold uppercase tracking-[0.06em] text-ink">
        <BarChart3 aria-hidden="true" className="size-5 text-violet" />
        {t("dashboard.production_pulse")}
      </h2>
      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-4">
        {cells.map((c) => (
          <div key={c.key} className="min-w-0">
            <div className="text-[26px] font-semibold text-ink">{c.value}</div>
            <div className="truncate text-[13px] text-ink">{c.label}</div>
            <div className="truncate text-[12px] text-violet-text">{c.sub}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function RecentActivity({ d }: { d: Dashboard }) {
  const { t, i18n } = useTranslation();
  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold uppercase tracking-[0.06em] text-ink">{t("dashboard.recent_activity")}</h2>
      {d.activity.length === 0 ? (
        <p className="mt-4 text-[14px] text-ink-2">{t("dashboard.activity_empty")}</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {d.activity.map((a, i) => (
            <li key={`${a.display_id}-${a.at}-${i}`} className="flex items-start gap-3">
              <Avatar name={a.actor_name ?? "Prisma"} size={36} />
              <div className="min-w-0">
                <div className="text-[14px] text-ink">
                  {t(`activity.${a.action.replace(/\./g, "_")}`, {
                    actor: a.actor_name ?? t("common.app_name"), id: a.display_id ?? "",
                    defaultValue: t("activity.generic", { actor: a.actor_name ?? "", id: a.display_id ?? "" }),
                  })}
                </div>
                <div className="text-[12px] text-ink-2">{a.at ? formatRelative(secondsSince(a.at), i18n.language) : ""}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
